import React from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-premium';

import { Icon, showDeleteDialog, TabFab, useSettings } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';
import { type ViewPageContentTabProps } from 'app/main/components/page-layouts/view/ViewPage';

import { showOrdersShippingChargesEditDialog } from '../../components/OrdersShippingChargesEditDialog';

type OrdersShippingChargesTabProps = Pick<ViewPageContentTabProps, 'refresh' | 'updateRefresh'>;

export default function OrdersShippingChargesTab({ refresh, updateRefresh }: OrdersShippingChargesTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	function addNewShippingCharges() {
		dispatch(
			showOrdersShippingChargesEditDialog({
				baseModel: {},
				isAdd: true,
				updateRefresh
			})
		);
	}

	function editShippingCharges(row) {
		dispatch(showOrdersShippingChargesEditDialog({ baseModel: row, updateRefresh }));
	}

	function deleteShippingCharges(row) {
		dispatch(
			showDeleteDialog({
				module: 'orders_shippingcharges',
				id: row.shch_uid,
				hardDelete: true,
				replace: false,
				updateRefresh
			})
		);
	}

	const columns: GridColDef[] = [
		{
			field: 'ctry_title',
			headerName: t('DETAILS_ORDERS_SHIPPINGCHARGES_FIELD_COUNTRY'),
			minWidth: 100,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'shch_value',
			type: 'number',
			headerName: t('DETAILS_ORDERS_SHIPPINGCHARGES_FIELD_VALUE'),
			minWidth: 100,
			flex: 1,
			renderCell: ViewTableUtils.renderCurrency
		},
		{
			field: 'shch_tot_value_from',
			type: 'number',
			headerName: t('DETAILS_ORDERS_SHIPPINGCHARGES_FIELD_TOT_VALUE_FROM'),
			minWidth: 100,
			flex: 1,
			renderCell: ViewTableUtils.renderCurrency
		},
		{
			field: 'shch_tot_value_to',
			type: 'number',
			headerName: t('DETAILS_ORDERS_SHIPPINGCHARGES_FIELD_TOT_VALUE_TO'),
			minWidth: 100,
			flex: 1,
			renderCell: ViewTableUtils.renderCurrency
		},
		{
			field: 'actions',
			type: 'actions',
			headerName: t('VIEW_TABLE_COLUMN_ACTIONS'),
			width: 100,
			cellClassName: 'actions',
			getActions: ({ row }) => [
				<GridActionsCellItem
					icon={<Icon icon="edit" />}
					label={t('VIEW_ITEM_BUTTON_EDIT')}
					onClick={() => editShippingCharges(row)}
				/>,
				<GridActionsCellItem
					icon={<Icon icon="delete" />}
					label={t('VIEW_ITEM_BUTTON_DELETE')}
					onClick={() => deleteShippingCharges(row)}
				/>
			]
		}
	];

	return (
		<Box className="w-full">
			<ViewTable
				module="orders_shippingcharges"
				columns={columns}
				refresh={refresh}
				updateRefresh={updateRefresh}
			/>
			<TabFab label={t('ORDERS_SHIPPINGCHARGES_ADD')} icon="add-circle" onClick={addNewShippingCharges} />
		</Box>
	);
}
